import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Observable, Subject } from 'rxjs';

// FCM Message interface
export interface FCMessage {
  title?: string;
  body?: string;
  data?: any;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class FcmService {
  private apiUrl = `${environment.apiUrl}/api/user`;
  private tokenSubject = new Subject<string | null>();
  public token$ = this.tokenSubject.asObservable();
  private messageSubject = new Subject<FCMessage>();
  public message$ = this.messageSubject.asObservable();
  private isInitialized = false;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone
  ) {}

  /**
   * Initialize FCM - Request permissions and get token
   * Call this after user logs in
   */
  async initializeFCM(): Promise<void> {
    console.log('=== FCM INIT START ===');
    
    if (this.isInitialized) {
      console.log('FCM already initialized, skipping...');
      return;
    }
    
    if (!Capacitor.isNativePlatform()) {
      console.log('FCM not available on web platform');
      return;
    }

    try {
      console.log('Importing FirebaseMessaging...');
      // Dynamic import to avoid errors on web
      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      
      console.log('Requesting FCM permissions...');
      // Request permission
      const permissionResult = await FirebaseMessaging.requestPermissions();
      console.log('Permission result:', permissionResult);
      
      if (permissionResult.receive === 'granted') {
        console.log('FCM permission granted');
        
        // Request local notifications permission for foreground notifications
        console.log('Requesting local notifications permission...');
        try {
          const localPermResult = await LocalNotifications.requestPermissions();
          console.log('Local notifications permission result:', JSON.stringify(localPermResult, null, 2));
          
          if (localPermResult.display !== 'granted') {
            console.warn('⚠️  Local notifications permission not fully granted:', localPermResult.display);
          } else {
            console.log('✅ Local notifications permission granted');
          }
        } catch (error) {
          console.error('❌ Error requesting local notification permissions:', error);
        }
        
        // Add listeners BEFORE getting token
        console.log('Adding FCM listeners...');
        
        // Listen for token refresh
        FirebaseMessaging.addListener('tokenReceived', (event) => {
          console.log('FCM Token refreshed:', event.token);
          this.ngZone.run(() => {
            this.tokenSubject.next(event.token);
          });
          this.registerToken(event.token);
        });

        // Listen for foreground notifications - SHOW THEM!
        FirebaseMessaging.addListener('notificationReceived', async (event: any) => {
          console.log('🔔 FCM Notification received (FOREGROUND):', JSON.stringify(event, null, 2));
          
          // FCM can structure notifications differently:
          // Option 1: event.notification (iOS, some Android)
          // Option 2: event itself has title/body (Android data messages)
          const notification = event.notification || event;
          const title = notification.title || event.title;
          const body = notification.body || event.body;
          const data = event.data || {};
          
          console.log('📋 Parsed notification - Title:', title, '| Body:', body, '| Data:', JSON.stringify(data));
          
          // Show local notification when app is in foreground
          if (title) {
            try {
              console.log('📱 Attempting to display local notification...');
              // Generate a 32-bit integer ID (max value: 2147483647)
              const notificationId = Math.floor(Math.random() * 2147483647);
              
              const result = await LocalNotifications.schedule({
                notifications: [
                  {
                    id: notificationId,
                    title: title,
                    body: body || '',
                    extra: data,
                    sound: 'default',
                    smallIcon: 'ic_stat_ic_notification',
                    channelId: 'default',
                  }
                ]
              });
              
              console.log('✅ Local notification scheduled successfully with ID:', notificationId, 'Result:', JSON.stringify(result));
            } catch (error) {
              console.error('❌ Error showing local notification:', error);
              console.error('Error details:', JSON.stringify(error, null, 2));
            }
          } else {
            console.warn('⚠️  No notification title found in event');
            console.warn('⚠️  Event keys:', Object.keys(event));
            console.warn('⚠️  Full event:', JSON.stringify(event, null, 2));
          }
          
          // Also emit for in-app handling (refresh UI)
          this.ngZone.run(() => {
            this.messageSubject.next(notification);
          });
        });

        // Handle notification tap (when app is in background/killed)
        FirebaseMessaging.addListener('notificationActionPerformed', (event: any) => {
          console.log('👆 Notification tapped:', event);
          this.ngZone.run(() => {
            this.messageSubject.next(event.notification || event);
          });
        });
        
        console.log('FCM listeners added successfully');
        
        // NOW get FCM token (after listeners are registered)
        console.log('Getting FCM token...');
        const tokenResult = await FirebaseMessaging.getToken();
        console.log('Token result:', tokenResult);
        
        if (tokenResult.token) {
          console.log('FCM Token:', tokenResult.token);
          this.ngZone.run(() => {
            this.tokenSubject.next(tokenResult.token);
          });
          // Register token with backend
          await this.registerToken(tokenResult.token);
        }
        
        this.isInitialized = true;
        console.log('✅ FCM initialized successfully');
      } else {
        console.warn('⚠️  FCM permission denied');
      }
    } catch (error) {
      console.error('❌ Error initializing FCM:', error);
    }
  }

  /**
   * Register FCM token with backend
   */
  private async registerToken(token: string): Promise<void> {
    try {
      console.log('Registering FCM token with backend...');
      await this.http
        .post(`${this.apiUrl}/fcm-token`, { token })
        .toPromise();
      console.log('FCM token registered with backend');
    } catch (error) {
      console.error('Error registering FCM token:', error);
    }
  }

  /**
   * Unregister FCM token (call on logout)
   */
  async unregisterToken(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await this.http.delete(`${this.apiUrl}/fcm-token`).toPromise();
      console.log('FCM token unregistered');
    } catch (error) {
      console.error('Error unregistering FCM token:', error);
    }
  }

  /**
   * Check if FCM is available
   */
  isAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }
}
